import { useUpdateSiteLinkMutation } from '@/shared/api'
import {
    UseUtmArgs,
    useUtmIdSelect,
} from '@/shared/ui/utm-builder-field/utmIdSelect'
import { Site } from '@/true-fsd/data/advertiser-account'
import { SiteLink, SiteLinkUrl } from '@/true-fsd/data/link'
import { UTMLink } from '@synapex/prisma/client'
import { capitalize } from '@synapex/shared2/utils/string'
import { Replace } from '@synapex/tools/typescript'
import { coerceUrl, UTM_KEYS, utmParam } from '@synapex/tools/utils'
import { AxiosError } from 'axios'
import { ReactNode } from 'react'
import { toast } from 'react-toastify'
import { CustomUtmBuilderField } from './CustomUtmBuilderField'
import {
    MainUrlInput,
    makeErrorMessage,
    type UseMainUrlInput,
    useMainUrlInput,
} from './MainUrlInput'
import { ShortUrlInput, useShortUrlInput } from './ShortUrlInput'
import classes from './UTMLinkEditDialog.module.scss'

const useUtmIdSelectBySearchParams = ({
    key,
    site_id,
    parsedValue,
    onChangeParsedValue,
}: Replace<
    UseUtmArgs,
    'stateBy',
    Pick<UseMainUrlInput, 'parsedValue' | 'onChangeParsedValue'>
>) => {
    // console.log({
    //     parsedValue: parsedValue
    //         ? Object.fromEntries(parsedValue.entries())
    //         : null,
    // })
    const param = utmParam(key)
    return useUtmIdSelect({
        key,
        site_id,
        stateBy: {
            type: 'name',
            selectedName: parsedValue?.get(param) ?? '',
            onSelectedNameChange: (newName) => {
                const newSp = new URLSearchParams(parsedValue ?? undefined)
                if (newName) {
                    newSp.set(param, newName)
                } else {
                    newSp.delete(param)
                }

                onChangeParsedValue(newSp)
            },
        },
    })
}

type UseLinkEditorArgs = {
    activeSite: Site
    erid?: string
    initial: {
        siteLinkUrl: null | SiteLinkUrl
        mainUrlParams: null | URLSearchParams
    }
}
const useLinkEditor = ({ activeSite, erid, initial }: UseLinkEditorArgs) => {
    const {
        onChangeParsedValue,
        parsedValue,
        getSyncedValue,
        errorMessage,
        value,
        ...rest
    } = useMainUrlInput(
        {
            hostname: activeSite.hostname,
            pathname: activeSite.pathname,
        },
        initial.mainUrlParams
    )

    const shortUrlInput = useShortUrlInput({ erid }, initial.siteLinkUrl)

    const { linkLocked, linkLockedMessage } = (() => {
        if (shortUrlInput.value) {
            return {
                linkLocked: true,
                linkLockedMessage:
                    'Невозможно изменить ссылку если с ней уже создана сокращенная ссылка',
            }
        }

        if (shortUrlInput.inputEnabled) {
            return {
                linkLocked: true,
                linkLockedMessage:
                    'Невозможно изменить ссылку если инпут сокращенной ссылки активен',
            }
        }

        return { linkLocked: false, linkLockedMessage: '' }
    })()

    const isEmpty = !parsedValue

    return {
        ...rest,
        value,
        getSyncedValue,
        errorMessage:
            errorMessage ??
            (isEmpty ? makeErrorMessage(activeSite) : undefined),
        utm: {
            medium: useUtmIdSelectBySearchParams({
                key: 'medium',
                site_id: activeSite.site_id,
                parsedValue,
                onChangeParsedValue,
            }),
            campaign: useUtmIdSelectBySearchParams({
                key: 'campaign',
                site_id: activeSite.site_id,
                parsedValue,
                onChangeParsedValue,
            }),
            source: useUtmIdSelectBySearchParams({
                key: 'source',
                site_id: activeSite.site_id,
                parsedValue,
                onChangeParsedValue,
            }),
            content: useUtmIdSelectBySearchParams({
                key: 'content',
                site_id: activeSite.site_id,
                parsedValue,
                onChangeParsedValue,
            }),
        },
        shortUrlInput,
        linkLocked,
        linkLockedMessage,
    }
}

export type LinkEditorProps = {
    utmLink: UTMLink
    groupId: number
    activeSite: Site
    siteLink: Pick<
        SiteLink,
        'siteLinkUrl' | 'site_id' | 'site_link_id' | 'utmLink'
    >
    erid?: string
    onSuccess: () => void
    RenderButtons: (props: {
        onSave: () => void
        disabled: boolean
    }) => ReactNode
}
export const LinkEditor = ({
    siteLink,
    activeSite,
    groupId,
    utmLink,
    erid,
    onSuccess,
    RenderButtons,
}: LinkEditorProps) => {
    const activeSiteId = activeSite.site_id

    const {
        value,
        onChange,
        utm,
        getSyncedValue,
        errorMessage,
        shortUrlInput,
        linkLocked,
        linkLockedMessage,
        onApply,
    } = useLinkEditor({
        activeSite,
        erid,
        initial: {
            mainUrlParams: coerceUrl(utmLink.baseUrl)?.searchParams ?? null,
            siteLinkUrl: siteLink.siteLinkUrl,
        },
    })

    const updateLink = useUpdateSiteLinkMutation({
        groupId,
        siteId: activeSiteId,
    })

    const utmIsLoading =
        utm.source.query.isLoading ||
        utm.medium.query.isLoading ||
        utm.campaign.query.isLoading ||
        utm.content.query.isLoading

    const updateSiteLink = () => {
        const syncedValue = getSyncedValue()

        console.log({ syncedValue })

        if (!syncedValue.ok) {
            return
        }

        updateLink.mutate(
            {
                siteLinkId: utmLink.siteLink_id,
                utmLink: syncedValue.normalizedValue,
                //in backend undefined will be coerced to null and link will be cleared
                siteLinkUrl: shortUrlInput.parsedValue,
                mediumId:
                    utm.medium.value === ''
                        ? undefined
                        : Number(utm.medium.value),
                campaignId:
                    utm.campaign.value === ''
                        ? undefined
                        : Number(utm.campaign.value),
                contentId:
                    utm.content.value === ''
                        ? undefined
                        : Number(utm.content.value),
                sourceId:
                    utm.source.value === ''
                        ? undefined
                        : Number(utm.source.value),
            },
            {
                onError: (error) => {
                    console.error(error)
                    toast.error(
                        (
                            (error as AxiosError).response?.data as Record<
                                string,
                                string
                            >
                        ).message || error.message
                    )
                },

                onSuccess,
            }
        )
    }

    return (
        <div>
            {utmIsLoading || (
                <MainUrlInput
                    onApply={onApply}
                    disabled={linkLocked}
                    value={value}
                    onChange={onChange}
                    errorMessage={errorMessage}
                />
            )}
            <ShortUrlInput
                {...shortUrlInput}
                className={classes.shortUrl}
                disabled={Boolean(errorMessage)}
            />
            <div className={classes.utmGrid}>
                {UTM_KEYS.map((utmKey) => {
                    const utmItem = utm[utmKey]

                    return (
                        utmItem.query.data && (
                            <CustomUtmBuilderField
                                key={utmKey}
                                disabled={linkLocked || Boolean(errorMessage)}
                                disabledMessage={linkLockedMessage}
                                createEntityHandler={utmItem.create}
                                items={utmItem.query.data}
                                fieldTitle={capitalize(utmKey)}
                                selectedId={utmItem.value}
                                inputValue={utmItem.newNameInput}
                                isLoading={utmItem.query.isLoading}
                                setInputValue={utmItem.setNewNameInput}
                                onSelectedIdChange={utmItem.onChange}
                            />
                        )
                    )
                })}
            </div>
            <RenderButtons
                onSave={updateSiteLink}
                disabled={
                    updateLink.isPending ||
                    shortUrlInput.inputEnabled ||
                    Boolean(errorMessage)
                }
            />
        </div>
    )
}
